import {
  BarElement,
  CategoryScale,
  Chart as ChartJS,
  Legend,
  LinearScale,
  LineElement,
  PointElement,
  TimeSeriesScale,
  Title,
  Tooltip
} from 'chart.js'
import { useContext, useEffect, useMemo, useState } from 'react'
import { getDateRangeLabels } from '../utils'
import { getUserRegistrationCount } from '../../../../utils/APIs/users'
import { Bar } from 'react-chartjs-2'
import DateRangePicker from '../DateRangePicker'
import Moaltip from '../../../Utilities/Tooltip'
import moment from 'moment'
import styles from '../Stats.module.css'
import { CSVLink } from 'react-csv'
import { PRODUCT_CODE } from '../../../../utils/ENV'
import { SiteConfigContext } from '../../../../context/siteConfigContext'

ChartJS.register(
  TimeSeriesScale,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend
)

const FreeRegistrationCounts = () => {
  const { freeEnabled } = useContext(SiteConfigContext)
  const [registrations, setRegistrations] = useState([])
  const [labels, setLabels] = useState([])
  const [data, setData] = useState(null)
  const [downloadData, setDownloadData] = useState([])
  const [startDate, setStartDate] = useState(
    moment().subtract(30, 'days').toDate()
  )
  const [endDate, setEndDate] = useState(moment().toDate())
  const formattedStartDate = moment(startDate).toISOString().split('T')[0]
  const formattedEndDate = moment(endDate).toISOString().split('T')[0]
  const [scaleType, setScaleType] = useState('timeseries')

  const options = {
    responsive: true,
    plugins: {
      legend: {
        position: 'top'
      },
      tooltip: {
        titleFont: {
          size: 18
        },
        bodyFont: {
          size: 24
        },
        padding: 12
      }
    },
    scales: {
      x: {
        stacked: true,
        type: scaleType,
        time: {
          unit: 'day'
        },
        ticks: {
          minRotation: 45,
          maxRotation: 45
        }
      },
      y: {
        stacked: true,
        ticks: {
          stepSize: 20
        }
      }
    }
  }

  const columns = useMemo(
    () => [
      {
        Header: 'Registration Date',
        accessor: 'registrationDate'
      },
      {
        Header: 'User Service ID',
        accessor: 'userService._id'
      },
      {
        Header: 'Customer Number',
        accessor: 'userService.customerNumber'
      },
      {
        Header: 'Name',
        accessor: 'userService.profile.name'
      },
      {
        Header: 'Email',
        accessor: 'userService.email'
      }
    ],
    []
  )

  useEffect(() => {
    loadData()
    handleLabels()
    setDownloadData([])
  }, [startDate, endDate])

  useEffect(() => {
    handleData()
  }, [registrations])

  const loadData = async () => {
    loadRegistrations()
  }

  const loadRegistrations = async () => {
    const response = await getUserRegistrationCount(
      moment(startDate).toISOString(),
      moment(endDate).toISOString(),
      false,
      true
    )
    if (response && response.status === 200) {
      setRegistrations(response.data)
    }
  }

  const handleLabels = () => {
    const dateLabels = getDateRangeLabels(startDate, endDate)
    setLabels(dateLabels)
  }

  const handleData = () => {
    setData({
      labels,
      datasets: [
        {
          label: 'Free List Registrations',
          data: registrations.map((each) => ({
            x: each._id,
            y: Math.round(each.count)
          })),
          backgroundColor: '#9e2a2b',
          pointStyle: false,
          stacked: true
        }
      ]
    })
  }

  if (labels.length <= 0 || data === null) {
    return (
      <div className={styles.wrapper}>
        <div className="flex items-center justify-center my-auto">
          <p className="animate-pulse">Loading...</p>
        </div>
      </div>
    )
  }

  const loadDownloadData = async () => {
    loadDownloadRegistrations()
  }

  const loadDownloadRegistrations = async () => {
    const response = await getUserRegistrationCount(
      moment(startDate).toISOString(),
      moment(endDate).toISOString(),
      true,
      true
    )
    if (response && response.status === 200) {
      const toDownload = []

      const getNestedValue = (obj, path) => {
        return path.split('.').reduce((acc, part) => acc && acc[part], obj)
      }

      response.data.forEach((data) => {
        const dataRow = {}
        for (let colIndex = 0; colIndex < columns.length; colIndex++) {
          const dataPoint = columns[colIndex].accessor
          if (dataPoint) {
            dataRow[columns[colIndex].Header] = getNestedValue(data, dataPoint)
          }
        }
        toDownload.push(dataRow)
      })
      setDownloadData(toDownload)
    }
  }

  if (!freeEnabled) return <></>

  return (
    <div className={styles.wrapper}>
      <header className="flex items-center gap-2">
        <h2>Free List Registrations</h2>
        <Moaltip>
          This chart shows daily free list registrations. Each bar represents
          the total count for that day. Hover for exact details.
        </Moaltip>
      </header>

      <Bar options={options} data={data} className="p-2" />
      <DateRangePicker
        startDate={startDate}
        endDate={endDate}
        setStartDate={setStartDate}
        setEndDate={setEndDate}
        scaleType={scaleType}
        setScaleType={setScaleType}
        disabled
      />
      {downloadData.length <= 0 ? (
        <button
          className="button-confirm mx-2 mb-2 w-fit"
          onClick={loadDownloadData}
          type="button"
        >
          Load Full Details for Export
        </button>
      ) : (
        <CSVLink
          className="button-confirm mx-2 mb-2 w-fit"
          data={downloadData}
          filename={`${PRODUCT_CODE}-free-registration-counts-${formattedStartDate}-${formattedEndDate}.csv`}
        >
          Download
        </CSVLink>
      )}
    </div>
  )
}

export default FreeRegistrationCounts
